import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { getBrandConfig } from '../config/brandConfigs';
import { getTranslations } from '../utils/translations';

const BrandContext = createContext();

export const BrandProvider = ({ children }) => {
    const lang = window.broadcastPlayerData?.language || 'en-US';
    const isSpanish = lang.toLowerCase().startsWith('es');
    const isVPV = window.location.origin.includes('visionparavivir.org');
    const isVPVBranded = isSpanish || isVPV;

    const brandConfig = useMemo(() => getBrandConfig(lang), [lang]);
    const translations = useMemo(() => getTranslations(), [lang]);

    const [customColor, setCustomColor] = useState(brandConfig.defaultColors.background);
    const [textColor, setTextColor] = useState(brandConfig.defaultColors.text);
    const [vpvTheme, setVpvTheme] = useState(isVPVBranded ? 'custom' : '');

    // Synchronize state with config changes (e.g., when lang changes)
    useEffect(() => {
        setCustomColor(brandConfig.defaultColors.background);
        setTextColor(brandConfig.defaultColors.text);
        if (isVPVBranded) {
            setVpvTheme('custom');
        } else {
            setVpvTheme('');
        }
    }, [brandConfig, isVPVBranded]);

    const value = {
        lang,
        isSpanish,
        isVPVBranded,
        brandConfig,
        translations,
        customColor,
        setCustomColor,
        textColor,
        setTextColor,
        vpvTheme,
        setVpvTheme,
        // Helper to get theme class based on state
        getThemeClass: () => {
            if (vpvTheme === 'orange') return 'vpv-branded vpv-orange';
            if (vpvTheme === 'blue') return 'vpv-branded vpv-blue';
            if (vpvTheme === 'gray') return 'vpv-branded vpv-gray';
            if (vpvTheme === 'custom') return 'vpv-branded vpv-custom-color';
            return brandConfig.themeClass;
        }
    };

    return (
        <BrandContext.Provider value={value}>
            {children}
        </BrandContext.Provider>
    );
};

export const useBrand = () => {
    const context = useContext(BrandContext);
    if (!context) {
        throw new Error('useBrand must be used within a BrandProvider');
    }
    return context;
};
